import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import {
  DataGridPremium,
  GridColDef,
  GridFeatureMode,
  GridRowModel,
  GridFilterModel,
  GridOverlay,
} from "@mui/x-data-grid-premium";
import FlexBetween from "../FlexBetween";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import DataGridPaginationFooter from "./DataGridPaginationFooter";

interface DataTableProps {
  dataRows: any[];
  dataColumns: GridColDef[];
  loading?: boolean;
  checkboxSelection?: boolean;
  className?: string;
  disableColumnFilter?: boolean;
  rowCount?: number;
  pageSizeOptions?: number[];
  paginationModel?: any;
  onPaginationModelChange?: (params: any) => void;
  paginationMode?: GridFeatureMode;
  tableHeight?: number;
  isFilterHide: boolean;
  errorMsg?: any;
  rowSelectionModel?: any;
  onRowSelectionModelChange?: (selectionModel: any) => void;
  pinnedColumns?: any;
  setPinnedColumns?: any;
  disableColumnPinning?: boolean;
  selectedIds?: any[];
  isIssuesDataAvailable?: any;
  setSelectedIds?: React.Dispatch<React.SetStateAction<any[]>>;
}

// ── ReadMoreCell ─────────────────────────────────────────────────────────────
const ReadMoreCell: React.FC<{
  displayContent: React.ReactNode;
  rawValue: any;
  rowId: string | number;
  isExpanded: boolean;
  onToggle: (id: string | number, expanded: boolean) => void;
}> = ({ displayContent, rawValue, rowId, isExpanded, onToggle }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Measure overflow after each render while in collapsed state
  useLayoutEffect(() => {
    if (contentRef.current && !isExpanded) {
      const el = contentRef.current;
      const overflow = el.scrollHeight > el.clientHeight + 2;
      setHasOverflow((prev) => (prev !== overflow ? overflow : prev));
    }
  }, [isExpanded, rawValue]);

  const tooltipTitle =
    !isExpanded && hasOverflow
      ? typeof rawValue === "string"
        ? rawValue
        : ""
      : "";

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Box sx={{ width: "100%", py: 0.5 }}>
        {/* Content box – clamped when collapsed */}
        <Box
          ref={contentRef}
          sx={
            !isExpanded
              ? {
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }
              : {
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }
          }
        >
          {displayContent ?? String(rawValue ?? "")}
        </Box>

        {/* Read More / Read Less toggle */}
        {hasOverflow && (
          <Typography
            variant="caption"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              display: "block",
              mt: 0.25,
              userSelect: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(rowId, !isExpanded);
            }}
          >
            {isExpanded ? "Read Less ▲" : "Read More ▼"}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const CustomHeaderFilter = ({
  column,
  filterValue,
  onFilterChange,
  isFilterHide,
}: any) => {
  const [inputValue, setInputValue] = useState(filterValue || "");

  const debouncedFilterChange = useCallback(
    debounce((value: any) => onFilterChange(value), 300),
    []
  );

  useEffect(() => {
    debouncedFilterChange(inputValue);
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [inputValue, debouncedFilterChange]);

  return (
    <Box display="flex" flexDirection="column">
      <Box className="header-name">{column?.headerName}</Box>
      <Box className={`textbox-group-wrapper ${isFilterHide ? "hide" : ""}`}>
        <Box className={`textbox-group`}>
          <TextField
            variant="outlined"
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`Filter ${column?.headerName}`}
          />
          <IconButton>
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const RootCauseAnalysisDataGrid: React.FC<DataTableProps> = ({
  dataRows,
  dataColumns,
  checkboxSelection = false,
  loading,
  className,
  disableColumnFilter,
  rowCount,
  pageSizeOptions,
  paginationModel,
  onPaginationModelChange,
  paginationMode = "client",
  tableHeight,
  isFilterHide,
  errorMsg,
  rowSelectionModel,
  onRowSelectionModelChange,
  pinnedColumns,
  disableColumnPinning = true,
  setPinnedColumns,
  selectedIds = [],
  setSelectedIds = () => {},
  isIssuesDataAvailable = true,
}) => {
  const { rowsPerPageOptions } = useRowsPerPage();
  const apiRef = useRef<GridApiPremium>(null!);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [filteredDataRows, setFilteredDataRows] = useState<any[]>(dataRows);
  const [maxHeight, setMaxHeight] = useState<any>(window.innerHeight - 290);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);
  const [keyColumns, setKeyColumns] = useState<any>([]);

  React.useEffect(() => {
    const handleResize = () => {
      setMaxHeight(window.innerHeight - 290);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Filter rows based on the filterModel
    const applyFilters = () => {
      let filteredRows = [...dataRows];

      filterModel.items.forEach((filter) => {
        const { field, value } = filter;
        const lowerCaseValue = value?.toString().toLowerCase() || "";

        filteredRows = filteredRows.filter((row) => {
          const cellValue = row[field]?.toString().toLowerCase() || "";
          return cellValue.includes(lowerCaseValue);
        });
      });

      setFilteredDataRows(filteredRows);
    };

    applyFilters();
  }, [filterModel, dataRows]);
  const previousSelectedIds = useRef(selectedIds);
  useEffect(() => {
    // Before data reload, persist the selectedIds
    previousSelectedIds.current = selectedIds;
  }, [selectedIds]);
  useEffect(() => {
    if (filteredDataRows && filteredDataRows.length && setSelectedIds) {
      setSelectedIds(previousSelectedIds.current); // Restore selectedIds after data reload
    }
  }, [filteredDataRows, setSelectedIds]);

  const handleFilterChange = (field: string, value: string) => {
    const newFilterItems = filterModel.items.filter(
      (item) => item.field !== field
    );

    if (value) {
      newFilterItems.push({
        field: field,
        operator: "contains",
        value,
      });
    }

    setFilterModel({ items: newFilterItems });
  };

  const columnWithFilters = dataColumns.map((column) => ({
    ...column,
    sortable: false,
    renderHeader: (params: any) => (
      <CustomHeaderFilter
        column={params.colDef}
        filterValue={
          filterModel.items.find((item) => item.field === column.field)
            ?.value || ""
        }
        onFilterChange={(value: any) => handleFilterChange(column.field, value)}
        isFilterHide={isFilterHide}
      />
    ),
  }));

  // ── Expanded rows state (Read More / Read Less) ───────────────────────────
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );

  const handleToggleExpand = useCallback(
    (id: string | number, expanded: boolean) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (expanded) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    []
  );

  // Recalculate row heights whenever expanded state changes
  useEffect(() => {
    setTimeout(() => {
      apiRef.current?.resetRowHeights?.();
    }, 0);
  }, [expandedRows]);

  // Columns enhanced with ReadMoreCell (skip action / id / boolean cols)
  const SKIP_FIELDS = new Set([
    "action",
    "actions",
    "srNo",
    "id",
    "__detail_panel_toggle__",
    "checkbox",
  ]);

  const processedColumns = columnWithFilters.map((col) => {
    if (SKIP_FIELDS.has(col.field) || col.type === "boolean") return col;
    return {
      ...col,
      renderCell: (params: any) => {
        const rowId = params.row?.rowId ?? params.row?.id ?? params.id;
        const isExpanded = expandedRows.has(rowId);
        const displayContent = col.renderCell
          ? col.renderCell(params)
          : params.value;
        return (
          <ReadMoreCell
            displayContent={displayContent}
            rawValue={params.value}
            rowId={rowId}
            isExpanded={isExpanded}
            onToggle={handleToggleExpand}
          />
        );
      },
    };
  });
  // ─────────────────────────────────────────────────────────────────────────

  const rowHeight =
    filteredDataRows.length < 5
      ? filteredDataRows.length * 60 + 171
      : filteredDataRows.length < 11
      ? filteredDataRows.length * 40 + 100
      : maxHeight + 95;

  const CustomFooter = ({ selectedIds }: any) => (
    <div className="datatable-footer">
      <strong>Selected IDs:</strong> [{selectedIds.join(", ")}]
      <Button
        className="btn-orange btn-dark btn-sm plus-btn-sm"
        onClick={() => setSelectedIds([])}
      >
        Reset
      </Button>
    </div>
  );
  const handleMoveColumns = () => {
    setPinnedColumns((prevState: any) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item)
      );
      const isKeyColumnAvailable = keyColumns.every((item: any) =>
        prevState.left.includes(item)
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable && isKeyColumnAvailable) {
        return {
          left: prevState.left.filter(
            (item: any) => !keyColumns.includes(item)
          ),
          right: prevState.right.filter(
            (item: any) => !columnsToAdd.includes(item)
          ),
        };
      } else {
        return {
          left: [...prevState.left, ...keyColumns],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };

  return (
    <FlexBetween height="100%" width="100%">
      <div style={{ width: "100%" }}>
        <Box
          className="position-relative"
          style={{
            height: pinnedColumns ? `${rowHeight}px` : "auto",
            maxHeight: pinnedColumns ? `${rowHeight}px` : "auto",
            minHeight: pinnedColumns ? `${rowHeight}px` : "auto",
            width: "100%",
            overflow: "auto",
          }}
        >
          {isIssuesDataAvailable &&
            pinnedColumns &&
            Object.entries(pinnedColumns).length > 0 &&
            filteredDataRows?.length > 0 && (
              <IconButton
                onClick={handleMoveColumns}
                className={`icon-pin-datagrid navbar-icon burger  ${
                  isAllColumnPinned ? "active" : ""
                }`}
              >
                <div className="bar bar1"></div>
                <div className="bar bar2"></div>
                <div className="bar bar3"></div>
              </IconButton>
            )}

          <DataGridPremium
            apiRef={apiRef}
            getRowHeight={(params) =>
              expandedRows.has(params.id) ? "auto" : null
            }
            className={`${className}`}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#146FD0",
                fontWeight: 700,
              },
              minHeight: "100px",
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                {},
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-sortIcon": {},
              "& .MuiDataGrid-virtualScroller": {
                maxHeight:
                  tableHeight && filteredDataRows.length < 6
                    ? tableHeight
                    : `calc(100vh - 230px)`,
                overflowY: "auto !important",
                overflowX: { md: "auto" },
                // minHeight: "60px",
              },
              "& .MuiDataGrid-overlayWrapper": {
                position: "static",
                height: "auto !important",
                width: "auto !important",
              },
              "& .MuiDataGrid-overlayWrapperInner": {
                height: "auto !important",
                width: "auto !important",
              },
            }}
            rows={filteredDataRows}
            columns={processedColumns}
            checkboxSelection={checkboxSelection}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(selectionModel) => {
              if (selectedIds) {
                const currentSelection = selectionModel;
                if (currentSelection.length > selectedIds.length) {
                  setSelectedIds((prevSelectedIds: any) => {
                    const updatedSelectedIds = new Set(prevSelectedIds);
                    selectionModel.forEach((id: any) => {
                      return updatedSelectedIds.add(id);
                    });
                    return Array.from(updatedSelectedIds);
                  });
                } else {
                  const deselectedId = selectedIds.find(
                    (id: any) => !currentSelection.includes(id)
                  );
                  setSelectedIds((prev: any) => {
                    return prev.filter((item: any) => item !== deselectedId);
                  });
                }
              }
            }}
            loading={loading}
            getRowId={(row: GridRowModel) => (row.rowId ? row?.rowId : row?.id)}
            disableColumnFilter={disableColumnFilter}
            disableColumnSelector={true}
            disableColumnMenu={false}
            getRowClassName={(params) =>
              params?.row?.is_derived ? "derived-column" : ""
            }
            pinnedColumns={pinnedColumns}
            disableColumnPinning={disableColumnPinning}
            disableRowGrouping={true}
            disableAggregation={true}
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) =>
              setFilterModel(newFilterModel)
            }
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={pageSizeOptions || rowsPerPageOptions}
            paginationMode={paginationMode}
            pagination
            rowCount={
              paginationMode === "client" ? filteredDataRows.length : rowCount
            }
            slots={{
              noRowsOverlay: errorMsg
                ? () => (
                    <GridOverlay>
                      <div
                        style={{
                          minHeight: "100px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 20px",
                        }}
                      >
                        {errorMsg}
                      </div>
                    </GridOverlay>
                  )
                : CustomNoRowsOverlay,
              footer: () => (
                <DataGridPaginationFooter pageSizeOptions={pageSizeOptions}>
                  {selectedIds &&
                  selectedIds.length > 0 &&
                  checkboxSelection ? (
                    <CustomFooter selectedIds={selectedIds} />
                  ) : (
                    <div />
                  )}
                </DataGridPaginationFooter>
              ),
            }}
          />
        </Box>
      </div>
    </FlexBetween>
  );
};
export default RootCauseAnalysisDataGrid;
